/*** IMPORTS FROM imports-loader ***/
var define = false;

/*jslint todo: true */

"use strict";

var ko = require("knockout");

var numberUtil = require("../util/number");
var stringUtil = require("../util/string");


//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Utility
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

//-------------------------------------------
// Create a function that will get error message
// from "this.behaviourData" or use the specified
// default message if not defined in "this.behaviourData".
function createGetErrorMessage(behaviourDataProperty, defaultMessage) {
    return function getErrorMessage() {
        var behaviourData = this.behaviourData;
        return (behaviourData && behaviourData[behaviourDataProperty]) || defaultMessage;
    };
}


//-------------------------------------------
// Extract the custom format from the specified
// "behaviourData" or return the default
// (if not specified by "behaviourData").
function getCustomFormatOrDefault(behaviourData, defaultFormat) {
    if (typeof behaviourData === "string") { return behaviourData; }
    return (behaviourData && typeof behaviourData.format === "string")
        ? behaviourData.format
        : defaultFormat;
}

//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// "NumericBehaviour" class
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

//-------------------------------------------
// Behaviour that ensures "this" observable
// resolves to a numeric value or null.
function NumericBehaviour() {
    return this; // keep JSLint happy
}


//-------------------------------------------
NumericBehaviour.prototype.format = function numericBehaviourFormat(value, format) {
    return (numberUtil.isNumber(value)
        ? numberUtil.formatNumber(value, format || getCustomFormatOrDefault(this.behaviourData))
        : "");
};


//-------------------------------------------
NumericBehaviour.prototype.parse = function numericBehaviourParse(value) {
    var num = numberUtil.parseNumber(value);
    return (isNaN(num) ? NumericBehaviour.prototype._getErrorMessage : num);
};


//-------------------------------------------
NumericBehaviour.prototype.validate = function numericBehaviourValidate(value) {
    var isValid = (value === null || value === undefined || numberUtil.isNumber(value));
    return (isValid ? null : NumericBehaviour.prototype._getErrorMessage);
};


//-------------------------------------------
NumericBehaviour.prototype._getErrorMessage = createGetErrorMessage("errorNumericValue", "Invalid number");



//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// "RepeatedValueBehaviour" class
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

//-------------------------------------------
// Behaviour that ensures "this" observable
// resolves to a value that is the same as
// another value (source specified in "behaviourData").
function RepeatedValueBehaviour() {
    this.defer = true;
}


//-------------------------------------------
RepeatedValueBehaviour.prototype.init = function repeatedValueBehaviourInit() {
    if (!this.behaviourData) { throw new Error("RepeatedValueBehaviour requires \"behaviourData.source\""); }
    var source = this.behaviourData.source;
    if (!ko.isSubscribable(source)) { throw new Error("RepeatedValueBehaviour requires \"behaviourData.source\" that is a Knockout subscribable"); }
    source.subscribe(RepeatedValueBehaviour.prototype._revalidate, this);
    if (!ko.isObservable(source.isValid)) { throw new Error("RepeatedValueBehaviour requires \"behaviourData.source.isValid\" that is a Knockout observable"); }
};


//-------------------------------------------
RepeatedValueBehaviour.prototype.validate = function repeatedValueBehaviourValidate(value) {
    var source = this.behaviourData.source;
    return (!source.isValid.peek() || source.peek() === value
        ? null
        : RepeatedValueBehaviour.prototype._getErrorMessage);
};


//-------------------------------------------
RepeatedValueBehaviour.prototype._getErrorMessage = createGetErrorMessage("errorRepeatedValue", "Value does not match");


//-------------------------------------------
RepeatedValueBehaviour.prototype._revalidate = function repeatedValueBehaviourRevalidate() {
    if (this._isActivated) {
        this.validate();
    }
};



//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// "RequiredBehaviour" class
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

//-------------------------------------------
// Behaviour that ensures "this" observable
// resolves to non-null value.
//
// "behaviourData.requiredWhen" is an optional
// function that can be specified to limit/throttle
// "required" validation. It is expected
// to match the following interface:
//
// interface RequiredWhenPredicate {
//     (): boolean;
// }
//
// When specified, "behaviourData.requiredWhen"
// will be wrapped in a Knockout pure computed
// (i.e. it will be rerun when any observable it
// depends on changes). Each change to the result
// of this computed will trigger revalidation.
//
// Examples:
//
// // always validate non-nullness of "a"
// var a = pli.observable("", null, pli.observable.behaviours.required);
//
// // only validate non-nullness of "b" when "a" is set to a valid number that is greater than 10
// var a = pli.observable(null, null, pli.observable.behaviours.numeric);
// var b = pli.observable(
//     "",
//     { requiredWhen: () => a.isValid() && a() > 10 },
//     pli.observable.behaviours.required);
//
// // only validate non-nullness of "c" when both "a" and "b" are valid
// var a = pli.observable(null, null, pli.observable.behaviours.numeric);
// var b = pli.observable(null, null, pli.observable.behaviours.numeric);
// var c = pli.observable(
//     "",
//     { requiredWhen: () => a.isValid() && b.isValid() },
//     pli.observable.behaviours.required);
//
function RequiredBehaviour() {
    this.defer = true;
}


//-------------------------------------------
RequiredBehaviour.prototype.init = function requiredBehaviourInit() {
    var requiredWhen = (this.behaviourData && this.behaviourData.requiredWhen);
    if (requiredWhen) {
        if (typeof requiredWhen !== "function") { throw new Error("RequiredBehaviour requires \"behaviourData.requiredWhen\" to be a function"); }
        this.behaviourData.requiredWhenComputed = ko.pureComputed(RequiredBehaviour.prototype._evaluateRequiredWhen, this);
        this.behaviourData.requiredWhenComputed.subscribe(RequiredBehaviour.prototype._revalidate, this);
    }
};


//-------------------------------------------
RequiredBehaviour.prototype.validate = function requiredBehaviourValidate(value) {
    // only proceed when "requiredWhen" is happy (or there is no "requiredWhen")
    // (result of last evaluation of "requiredWhen" is stored in "requiredWhenComputed")
    var requiredWhenComputed = (this.behaviourData && this.behaviourData.requiredWhenComputed);
    if (requiredWhenComputed && !requiredWhenComputed()) {
        return null; // skip this validation, reporting it as valid for now
    }

    // check for empty string, empty array, and null/undefined
    var isValid = (typeof value === "string"
        ? !stringUtil.isNullOrWhitespace(value)
        : Array.isArray(value)
            ? value.length !== 0
            : value !== null && value !== undefined);
    return (isValid ? null : RequiredBehaviour.prototype._getErrorMessage);
};


//-------------------------------------------
RequiredBehaviour.prototype._getErrorMessage = createGetErrorMessage("errorRequiredValue", "Required value");


//-------------------------------------------
RequiredBehaviour.prototype._evaluateRequiredWhen = function requiredBehaviourEvaluateRequiredWhen() {
    return !!this.behaviourData.requiredWhen();
};


//-------------------------------------------
RequiredBehaviour.prototype._revalidate = function requiredBehaviourRevalidate() {
    if (this._isActivated) {
        this.validate();
    }
};



//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// "ValidateBehaviour" class
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

//-------------------------------------------
// Behaviour that ensures the specified validation
// function runs when "this" observable is validated.
function ValidateBehaviour(validateFunction) {
    this.defer = !!validateFunction.defer;
    this.validate = validateFunction;
}



//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// "PliTextBoxObservable" class (and helper functions)
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

//-------------------------------------------
// If source contains definition of the specified
// method, make sure target does not contain
// it yet and then add it to target.
function extractSingleBehaviourMethod(source, target, functionName) {
    if (typeof source[functionName] === "function") {
        if (typeof target[functionName] === "function") { throw new Error("Only one behaviour with \"" + functionName + "\" method is allowed"); }
        target[functionName] = source[functionName];
    }
}


/**
 * Return normalized version of the specified value:
 * - undefined => null
 * - null => null
 * - (dateValue) => new Date(dateValue) -- clone date values because
 *     they are mutable objects so changes made by Pli observable
 *     would immediately change caller's value as well (when referencing
 *     the same instance)
 * - (value) => (value)
 */
function normalizeValue(value) {
    return (value instanceof Date)
        ? new Date(value)
        : (value === undefined ? null : value);
}


//-------------------------------------------
// Recursively load behaviours from the specified
// source + trigger init methods (in the context
// of the specified "observable").
function loadBehaviours(source, target, observable) {
    var behaviour, i;

    // check for registered behaviour
    if (typeof source === "string") {
        behaviour = ko.observablePliTextBox.behaviours[source];
        if (!behaviour) { throw new Error("Behaviour \"".concat(source, "\" is not known (did you register it in \"ko.observablePliTextBox.behaviours\"?)")); }
        loadBehaviours(behaviour, target, observable);

    // check for validate behaviour
    } else if (typeof source === "function") {
        loadBehaviours(new ValidateBehaviour(source), target, observable);

    // check for an array
    } else if (Object.prototype.toString.call(source) === "[object Array]") {
        for (i = 0; i < source.length; i += 1) {
            loadBehaviours(source[i], target, observable);
        }

    // check for behaviour object
    } else if (source && typeof source === "object") {
        target.push(source);
        extractSingleBehaviourMethod(source, target, "format");
        extractSingleBehaviourMethod(source, target, "initBinding");
        extractSingleBehaviourMethod(source, target, "parse");
        extractSingleBehaviourMethod(source, target, "show");
        extractSingleBehaviourMethod(source, target, "updateBinding");
        if (typeof source.init === "function") {
            source.init.call(observable);
        }
    }

    return target;
}


//-------------------------------------------
// Create a function that calls "targetFn"
// (preserving "this" context) and ensuring
// that "this._internalUpdate" will be
// true while "targetFn" is running (preventing
// execution of "_onObservableChanged").
function createInternalUpdateGuard(targetFn) {
    return function internalUpdateGuard() {
        var originalValue = this._internalUpdate;
        this._internalUpdate = true;
        try {
            return targetFn.apply(this, arguments);
        } finally {
            this._internalUpdate = originalValue;
        }
    };
}


//-------------------------------------------
// Find index of the specified controller in
// the specified collection of controllers.
function findControllerIndex(c, controllers) {
    var i, l;
    for (i = 0, l = controllers.length; i < l; i += 1) {
        if (c === controllers[i]) {
            return i;
        }
    }
    return -1;
}


//-------------------------------------------
// Initialize a new instance of PliTextBoxObservable.
// See: https://github.com/pandell/Pli/wiki/Text-Box-Observable#configuration
function PliTextBoxObservable(initialValue, behaviourData) {
    // properties
    var allowUndefined = (behaviourData && behaviourData.allowUndefined);
    var o = ko.observable(allowUndefined && initialValue === undefined ? undefined : null);
    o.behaviourData = normalizeValue(behaviourData);
    o.isValid = ko.observable(true);
    o._behaviours = [];
    o._controllers = [];
    o._internalUpdate = false;
    o._isActivated = false;
    o._errorMessage = null;
    o._raw = null; // will only be not-null (string) when parsing fails

    // methods
    var p = PliTextBoxObservable.prototype;
    o.format = p.format;
    o.load = p.load;
    o.peekValue = p.peekValue;
    o.reset = p.reset;
    o.setErrorMessage = p.setErrorMessage;
    o.show = p.show;
    o.validate = p.validate;
    o._addController = p._addController;
    o._initBinding = p._initBinding;
    o._loadText = p._loadText;
    o._removeController = p._removeController;
    o._updateBinding = p._updateBinding;
    o._updateView = p._updateView;

    // make sure state is updated when main observable changes
    o.subscribe(p._onObservableChanged, o);

    // load and initialize behaviours
    var behavioursSpec = Array.prototype.slice.call(arguments, 2);
    behavioursSpec.unshift(o.behaviourData && o.behaviourData.behaviours); // [behaviourData.behaviours, argument[2], ..., argument[n]]
    loadBehaviours(behavioursSpec, o._behaviours, o);

    // set initial value of our new observable
    o(allowUndefined ? initialValue : normalizeValue(initialValue)); // will trigger validation and updateView if not null
    return o;
}


//-------------------------------------------
// Convert current value of this observable to
// a string using the specified or the default
// format. If current value is not valid, returns
// the source string (before validation/parsing).
//
// Does not mutate state.
PliTextBoxObservable.prototype.format = function textBoxObservableFormat(customFormat) {
    var value = this.peek();
    if (value === null || value === undefined) {
        return (this._raw || "");
    }

    return (this._behaviours.format
        ? this._behaviours.format.call(this, value, customFormat)
        : value.toString());
};


//-------------------------------------------
// Loads the value of the observable using either a parsed or raw value.
// See: https://github.com/pandell/Pli/wiki/Text-Box-Observable#load-method
//
// Uses internal update guard (see "createInternalUpdateGuard").
PliTextBoxObservable.prototype.load = createInternalUpdateGuard(
    function textBoxObservableLoad(value, ignoreFocus) {
        // update root observable + _raw
        var allowUndefined = (this.behaviourData && this.behaviourData.allowUndefined);
        value = (allowUndefined ? value : normalizeValue(value));
        var errorSpec = PliTextBoxObservable.prototype._validateValue.call(this, value);
        var updateNeeded;
        if (errorSpec === null) { // specified value is valid, set it (and reset validation error, if any)
            updateNeeded = (PliTextBoxObservable.prototype._applyErrorSpec.call(this, null) || this.peek() !== value);
            this._raw = null;
            this(value); // "_onObservableChanged" will be skipped because of internal update guard
        } else { // specified value is not valid, set it as text (will attempt to parse & validate it)
            updateNeeded = PliTextBoxObservable.prototype._loadText.call(this, value);
        }

        // update view (if needed)
        if (updateNeeded) {
            this._updateView(ignoreFocus);
        }
    }
);


//-------------------------------------------
// Return value of "this" observable using
// "observable.peek" if not null; otherwise
// return last (string) value that we
// attempted to parse.
PliTextBoxObservable.prototype.peekValue = function textBoxObservablePeekValue() {
    var v = this.peek();
    return (v === null ? this._raw : v);
};


//-------------------------------------------
// Clear the state and value of "this" observable.
// See: https://github.com/pandell/Pli/wiki/Text-Box-Observable#reset-method
//
// Uses internal update guard (see "createInternalUpdateGuard").
PliTextBoxObservable.prototype.reset = createInternalUpdateGuard(
    function textBoxObservableReset(skipUpdateView, resetRaw, resetValue) {
        if (this._isActivated || !this.isValid.peek() || this.peekValue() !== null) {
            PliTextBoxObservable.prototype._applyErrorSpec.call(this, null); // reset validation error (if any)
            this._isActivated = false;
            this._raw = (resetRaw === undefined ? null : resetRaw);
            this(resetValue === undefined ? null : resetValue); // "_onObservableChanged" will be skipped because of internal update guard
            if (!skipUpdateView) {
                this._updateView(true); // true -> ignoreFocus (i.e., update view even if currently focused)
            }
        }
    }
);


//-------------------------------------------
// Set the error mesage for "this" observable.
// See: https://github.com/pandell/Pli/wiki/Text-Box-Observable#seterrormessage-method
PliTextBoxObservable.prototype.setErrorMessage = function textBoxObservableSetErrorMessage(errorSpec) {
    var updateNeeded = PliTextBoxObservable.prototype._applyErrorSpec.call(this, errorSpec);
    if (updateNeeded) {
        this._updateView();
    }

    return !this.isValid.peek();
};


//-------------------------------------------
// Display selection for for "this" observable
// (if defined by one of its behaviours).
// See: https://github.com/pandell/Pli/wiki/Text-Box-Observable#show-method
//
// settings.onShowDone: (required, function)
//     reference of a function that will be called
//     when selection form is closed; the function
//     will receive selected value as parameter
//     (or "undefined" if the form was cancelled)
//
// settings.onShowDoneContext: (optional, object)
//     optional object that will be used to set
//     context ("this") in "settings.onShowDone"
//     function (when called).
//
// settings.$input: (optional, jQuery object)
//     DOM element that triggered this "show" call;
//     can be used for position calculation, focus
//     restoration, etc.
PliTextBoxObservable.prototype.show = function textBoxObservableShow(settings) {
    if (this._behaviours.show) {
        if (!settings || typeof settings.onShowDone !== "function") { throw new Error("settings.onShowDone is required (function)"); }
        return this._behaviours.show.call(
            this, // preserve "this"
            settings
        );
    }
};


//-------------------------------------------
// Verify that the value of "this" observable is correct.
// See: https://github.com/pandell/Pli/wiki/Text-Box-Observable#validate-method
PliTextBoxObservable.prototype.validate = function textBoxObservableValidate(skipActivation) {
    // apply pending changes (there should be
    // at most one controller with pending
    // changes at any time, but we need to check
    // them all); this will ensure that we are
    // validating the right value
    var i, l;
    for (i = 0, l = this._controllers.length; i < l; i += 1) {
        this._controllers[i]._applyChanges(); // will do nothing if this controller has no pending changes
    }

    if (!skipActivation) {
        this._isActivated = true;
    }

    var value = this.peek();
    if (value !== null || this._raw === null) { // only re-validate if value parsing succeeded
        var errorSpec = PliTextBoxObservable.prototype._validateValue.call(this, value);
        this.setErrorMessage(errorSpec);
    }

    return this.isValid.peek();
};


//-------------------------------------------
// Add the specified controller to this
// observable's controllers collection.
PliTextBoxObservable.prototype._addController = function textBoxObservableAddController(c) {
    if (findControllerIndex(c, this._controllers) === -1) {
        this._controllers.push(c);
    }
};


//-------------------------------------------
// Update validation-error state of "this" observable.
// Returns true if state of this observable changed
// (i.e. view update is needed), false otherwise.
PliTextBoxObservable.prototype._applyErrorSpec = function textBoxObservableApplyErrorSpec(errorSpec) {
    var isValid, message;
    if (errorSpec) {
        isValid = false;
        message = (typeof errorSpec === "function" ? errorSpec.call(this) : errorSpec.toString());
        message = (message || "Unknown error");
    } else {
        isValid = true;
        message = null;
    }

    if (isValid !== this.isValid.peek() || message !== this._errorMessage) {
        this._errorMessage = message;
        this.isValid(isValid);
        return true;
    }

    return false;
};


//-------------------------------------------
// Allow one of the behaviours associated with
// this text box observable to contribute
// to binding initialization.
PliTextBoxObservable.prototype._initBinding = function textBoxObservableInitBinding(textBoxController, allBindings) {
    if (this._behaviours.initBinding) {
        return this._behaviours.initBinding.call(this, textBoxController, allBindings);
    }
};


//-------------------------------------------
// Load the specified text as value of this
// observable. Attempts to parse the text
// using behaviour parser (if any). Returns
// true if state of this observable changed
// (i.e. view update is needed), false otherwise.
//
// Uses internal update guard (see "createInternalUpdateGuard").
PliTextBoxObservable.prototype._loadText = createInternalUpdateGuard(
    function textBoxObservableLoadText(text) {
        if (text !== null && text !== undefined) {
            text = text.toString();
        }

        // step 1: parse text
        var value = text;
        var raw = null;
        var updateNeeded = false;
        var validationNeeded = true;
        if (typeof this._behaviours.parse === "function") {
            value = this._behaviours.parse.call(this, value);
            if (typeof value === "function") { // parsing failed
                updateNeeded = PliTextBoxObservable.prototype._applyErrorSpec.call(this, value);
                validationNeeded = false;
                value = null;
                raw = text;
            }
        }

        // step 2: validate (if needed)
        if (validationNeeded) {
            var errorSpec = PliTextBoxObservable.prototype._validateValue.call(this, value);
            updateNeeded = PliTextBoxObservable.prototype._applyErrorSpec.call(this, errorSpec);
        }

        // step 3: update main observable and _raw (if needed)
        if (this.peek() !== value || (value === null && this._raw !== raw)) {
            updateNeeded = true;
            this._raw = raw;
            this(value); // "_onObservableChanged" will be skipped because of internal update guard
        }

        return updateNeeded;
    }
);


//-------------------------------------------
// Synchronize state of "this" observable
// with its new value.
//
// Performs no actions if called due to the actions
// of a function wrapped by the internal update guard
// (see "createInternalUpdateGuard").
PliTextBoxObservable.prototype._onObservableChanged = function textBoxObservableOnObservableChanged(value) {
    var o = this;
    if (o._internalUpdate) { return; }

    if (value === undefined && (!o.behaviourData || !o.behaviourData.allowUndefined)) {
        throw new Error("Invalid value, \"ko.observablePliTextBox\" does not accept \"undefined\" by default -- either use \"load\" method or set \"behaviourData.allowUndefined\"");
    }
    if (PliTextBoxObservable.prototype._validateValue.call(o, value) !== null) {
        throw new Error("Invalid value, \"ko.observablePliTextBox\" only accepts valid values -- please use \"load\" method instead");
    }

    o._raw = null;
    PliTextBoxObservable.prototype._applyErrorSpec.call(o, null); // reset last validation error (if any)
    o._updateView();
};


//-------------------------------------------
// Remove the specified controller from "this"
// observable's collection of controllers.
PliTextBoxObservable.prototype._removeController = function textBoxObservableRemoveController(c) {
    var i = findControllerIndex(c, this._controllers);
    if (i !== -1) {
        this._controllers.splice(i, 1);
    }
};


/**
 * Allows one of the behaviours associated with this text box observable to contribute to binding update.
 *
 * @param textBoxController
 *      Associated TextBoxController instance.
 * @param allBindings
 *      Knockout 'allBindings' property passed to update handler.
 * @returns
 *      Value returned by updateBinding behaviour if defined; otherwise 'undefined'.
 */
PliTextBoxObservable.prototype._updateBinding = function textBoxObservableUpdateBinding(textBoxController, allBindings) {
    if (this._behaviours.updateBinding) {
        return this._behaviours.updateBinding.call(this, textBoxController, allBindings);
    }
};


//-------------------------------------------
// Update view of all controllers currently
// associated with this observable.
PliTextBoxObservable.prototype._updateView = function textBoxObservableUpdateView() {
    var i, l, c;
    for (i = 0, l = this._controllers.length; i < l; i += 1) {
        c = this._controllers[i];
        c.updateView.apply(c, arguments);
    }
};


//-------------------------------------------
// Validate the specified value against all
// behaviours of "this" observable.
//
// Does not mutate state, only returns an
// errorSpec (null if valid, function or
// a string otherwise).
PliTextBoxObservable.prototype._validateValue = function textBoxObservableValidateValue(value) {
    var activated = this._isActivated;
    var behaviours = this._behaviours;

    var i, l, b, errorSpec;
    for (i = 0, l = behaviours.length; i < l; i += 1) {
        b = behaviours[i];
        if (b && (activated || !b.defer) && typeof b.validate === "function") {
            errorSpec = b.validate.call(this, value);
            if (errorSpec !== null) {
                return errorSpec;
            }
        }
    }
    return null;
};



//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Initialization
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

PliTextBoxObservable.shared = {
    createGetErrorMessage: createGetErrorMessage,
    getCustomFormatOrDefault: getCustomFormatOrDefault
};
PliTextBoxObservable.behaviours = {
    numeric: new NumericBehaviour(),
    repeatedValue: new RepeatedValueBehaviour(),
    required: new RequiredBehaviour()
};

module.exports = PliTextBoxObservable;




//////////////////
// WEBPACK FOOTER
// ./~/pli/forms-knockoutcontrols/PliTextBoxObservable.js
// module id = 20
// module chunks = 49